(ns minorodata.components.pipeline-editor.utils
  (:require
   ["lucide-react" :refer [Binary Braces BrainCircuit CircleHelp Clock DatabaseIcon
                           FileSpreadsheet GitCommitHorizontal Layers LayoutList MapPin
                           RotateCw Save Sheet SquareArrowOutUpRightIcon Trash2Icon UndoIcon]]
   ["react" :as react]
   [clojure.string :as str]
   [minorodata.app.handlers :refer [dispatch]]
   [minorodata.app.utils :refer [assoc-some format-date]]
   [minorodata.components.monaco.diff-editor :as monaco.diff-editor]
   [minorodata.components.pipeline-editor.routes :refer [extract-edit-route
                                                        extract-legacy-route]]
   [minorodata.components.spinner.core :as spinner]
   [mold-forms.core :as form-component :refer [model->fields]]
   [mold-forms.model :refer [->form-model]]
   [re-posh.core :as re-posh]
   [reagent.core :as reagent]
   [reitit.frontend.easy :as rt.easy]
   [ui.alert :as alert]
   [ui.button :as button]
   [ui.chip :refer [chip]]
   [ui.collapsible :refer [collapsible-content collapsible-root
                           collapsible-trigger]]
   [ui.dialog :refer [dialog-actions dialog-close dialog-content dialog-root
                      dialog-title]]
   [ui.drawer]
   [ui.fields :as fields]
   [ui.popover :refer [popover-actions popover-content popover-root
                       popover-trigger]]
   [ui.tooltip :as tooltip]
   [ui.typography :as typography]
   [ui.utils :refer [clsx]]))


(defn open-tab [{:keys [id group-id type name preview]}]
  (dispatch [:pipeline-editor.events/open-tab
             (assoc-some
              {:type     type
               :id       id
               :group-id group-id
               :name     name}
              :preview  preview)]))

(defn navigate-transform-tab
  ([id group-id name]
   (navigate-transform-tab id group-id name false))
  ([id group-id name preview?]
   (open-tab {:id id :group-id group-id :type "transform" :name name :preview preview?})))

(defn navigate-transform-tab-preview [id group-id name]
  (open-tab {:id id :group-id group-id :type "transform" :name name :preview true}))

(defn navigate-table-tab [id group-id name]
  (open-tab {:id id :group-id group-id :type "table" :name name}))

(defn navigate-table-tab-preview [id group-id name]
  (open-tab {:id id :group-id group-id :type "table" :name name :preview true}))

(defn navigate-extract-tab
  ([id group-id name]
   (navigate-extract-tab id group-id name false))
  ([id group-id name preview?]
   (open-tab {:id id :group-id group-id :type "extract" :name name :preview preview?})))

(defn extract-edit-href [source-id extract-id]
  (apply rt.easy/href (extract-edit-route source-id extract-id)))

(defn navigate-to-extract-edit [source-id extract-id]
  (dispatch (into [:app.events/navigate] (extract-edit-route source-id extract-id))))

(defn extract-legacy-href [source-id extract-id]
  (apply rt.easy/href (extract-legacy-route source-id extract-id)))

(defn navigate-to-extract-legacy [source-id extract-id]
  (dispatch (into [:app.events/navigate] (extract-legacy-route source-id extract-id))))

(defn navigate-analytics-model-tab [id group-id name]
  (open-tab {:id id :group-id group-id :type "model" :name name}))

(defn navigate-tab [tab]
  (open-tab tab))

(defn select-tab [id]
  (dispatch [:pipeline-editor.events/select-tab id]))

(defn close-tab [id]
  (dispatch [:pipeline-editor.events/close-tab id]))

(def tab-icons
  {:transform LayoutList
   :table     Sheet
   :extract   DatabaseIcon
   :model     BrainCircuit
   :all       Layers
   :group     LayoutList
   :source    DatabaseIcon
   :webform   FileSpreadsheet})

;; group/source render as bordered (rather than filled) chips — see `tab-icon`.
(def ^:private bordered-tab-types #{"group" "source"})

(defn tab-icon [{:keys [type size class muted?]
                 :or {size 16}}]
  (when-let [icon (get tab-icons (keyword type))]
    [:div {:class (clsx "p-0.5 rounded"
                        (if muted?
                          ;; inactive/greyed variant — keep the bordered-vs-filled
                          ;; shape but drop the brand colour
                          (if (bordered-tab-types type)
                            "border border-kleene-text/20 *:text-kleene-text/40"
                            "bg-kleene-text/10 *:text-kleene-text/40")
                          {"bg-transforms/20 *:text-transforms" (= type "transform")
                           "bg-tables/20 *:text-tables" (= type "table")
                           "bg-timelines *:text-timelines *:brightness-50" (= type "timeline")
                           "bg-extracts/20 *:text-extracts" (= type "extract")
                           "bg-kleene-secondary-05 *:text-kleene-secondary-v2" (= type "model")
                           "bg-kleene-text/10 *:text-kleene-text-secondary" (= type "all")
                           ;; group/source mirror the global-search treatment (bordered,
                           ;; sharing the transform/extract brand colours).
                           "border border-transforms *:text-transforms" (= type "group")
                           "border border-extracts *:text-extracts" (= type "source")
                           "bg-webforms/20 *:text-webforms" (= type "webform")})
                        class)}
     [:> icon {:size size}]]))

(defn group-data-type [data-type]
  (let [t (str/upper-case data-type)]
    (case t
      ;; Numeric types
      ("INTEGER" "INT64" "INT" "SMALLINT" "BIGINT" "NUMERIC" "NUMBER" "DECIMAL" "BIGNUMERIC"
                 "FLOAT" "FLOAT4" "FLOAT8" "FLOAT64" "DOUBLE" "DOUBLE PRECISION" "REAL")
      :numeric

      ;; String and binary types
      ("STRING" "VARCHAR" "CHAR" "CHARACTER" "TEXT" "NCHAR" "NVARCHAR" "BINARY" "VARBINARY" "BYTES")
      :string-binary

      ;; Date and time types
      ("DATE" "DATETIME" "TIME" "TIMESTAMP" "TIMESTAMP_LTZ" "TIMESTAMP_NTZ" "TIMESTAMP_TZ" "TIMESTAMPTZ")
      :date-time

      ;; Logical (boolean) types
      ("BOOLEAN" "BOOL")
      :logical

      ;; Semi-structured types
      ("VARIANT" "OBJECT" "ARRAY" "STRUCT" "JSON" "SUPER")
      :semi-structured

      ;; Geospatial types
      "GEOGRAPHY"
      :geospatial

      ;; Default fallback
      :unknown)))

(defn numeric-icon [props]
  (let [{:keys [size] :or {size 24}} (js->clj props :keywordize-keys true)]
    (reagent/as-element
     [:svg {:width size :height size :viewBox "0 0 24 24" :fill "none" :xmlns "http://www.w3.org/2000/svg"}
      [:path {:d "M3 10L6 8V16" :stroke "currentColor" :stroke-width "2" :stroke-linecap "round" :stroke-linejoin "round"}]
      [:path {:d "M14 15.742H10C10 11.742 14 12.742 14 9.74201C14 8.24201 12 7.24201 10 8.74201" :stroke "currentColor" :stroke-width "2" :stroke-linecap "round" :stroke-linejoin "round"}]
      [:path {:d "M17.5 8.40253C19.2 7.40253 21 8.40253 21 9.90253C21 10.433 20.7893 10.9417 20.4142 11.3167C20.0391 11.6918 19.5304 11.9025 19 11.9025C19.5304 11.9025 20.0391 12.1132 20.4142 12.4883C20.7893 12.8634 21 13.3721 21 13.9025C21 15.7025 19 16.9025 17 15.4025" :stroke "currentColor" :stroke-width "2" :stroke-linecap "round" :stroke-linejoin "round"}]])))

(defn text-icon [props]
  (let [{:keys [size] :or {size 24}} (js->clj props :keywordize-keys true)]
    (reagent/as-element
     [:svg {:width size :height size :viewBox "0 0 24 24" :fill "none" :xmlns "http://www.w3.org/2000/svg"}
      [:path {:d "M3 16V12M7 16V12M3 12V10C3 8.89543 3.89543 8 5 8C6.10457 8 7 8.89543 7 10V12M3 12H7"
              :stroke "currentColor" :stroke-width "2" :stroke-linecap "round" :stroke-linejoin "round"}]
      [:path {:d "M21 8H19C17.8954 8 17 8.89543 17 10V14C17 15.1046 17.8954 16 19 16H21"
              :stroke "currentColor" :stroke-width "2" :stroke-linecap "round" :stroke-linejoin "round"}]
      [:path {:d "M12 12C13.1046 12 14 11.1046 14 10C14 8.89543 13.1046 8 12 8H10V12M12 12C13.1046 12 14 12.8954 14 14C14 15.1046 13.1046 16 12 16H10V12M12 12H10"
              :stroke "currentColor" :stroke-width "2" :stroke-linecap "round" :stroke-linejoin "round"}]])))

(defn icon-wrapper [props icon]
  [:div {:class (clsx "flex items-center justify-center w-6 h-6 rounded-lg bg-kleene-secondary-05 text-kleene-secondary-v2" (:class props))}
   [:> icon {:size (or (:size props) 16)}]])

(defn data-type-icon [type]
  (let [type (group-data-type type)]
    (case type
      :numeric (fn [props] [icon-wrapper props numeric-icon])
      :string-binary (fn [props] [icon-wrapper props text-icon])
      :date-time (fn [props] [icon-wrapper props Clock])
      :logical (fn [props] [icon-wrapper props Binary])
      :semi-structured (fn [props] [icon-wrapper props Braces])
      :geospatial (fn [props] [icon-wrapper props MapPin])
      (fn [props] [icon-wrapper props CircleHelp]))))

(defn update-item [{:keys [type master-id group-id transform actions selected? on-select grouped?]}]
  (let [live-transform @(re-posh/subscribe [:live-transform/pull [:live-transform/master-id master-id]])
        group @(re-posh/subscribe [:pipeline-group/pull [:pipeline-group/id (or group-id (:group-id transform))]])
        removed-from-live (and (not live-transform)
                               (not= type "CREATE"))]

    [collapsible-root
     [collapsible-trigger {:class (clsx "z-10 hover:bg-kleene-hover-background transition-colors radix-state-open:bg-kleene-selected-background"
                                        (if grouped?
                                          "px-3 py-2 has-[&_.action-button:hover]:bg-transparent"
                                          "p-2 bg-kleene-hover-background/30 has-[&_.action-button:hover]:bg-kleene-hover-background/30 rounded-md")
                                        {"bg-kleene-warning-background hover:bg-kleene-warning-background" removed-from-live})}
      [:div {:class "flex items-center justify-between gap-2 min-w-0"}
       (when on-select
         [fields/checkbox {:checked   selected?
                           :class     "shrink-0"
                           :on-click  #(.stopPropagation %)
                           :on-change #(on-select)}])
       [:div {:class "flex flex-col flex-1 min-w-0 overflow-hidden"}
        [:div {:class "flex items-center gap-2 min-w-0 overflow-hidden"}
         [:span {:class "truncate min-w-0"}
          (or (:name transform) (:live-transform/name live-transform))]
         [chip {:variant (case type
                           "CREATE" :success
                           "DELETE" :error
                           :default)
                :class "bg-kleene-background shrink-0"}
          (some-> type str/capitalize)]]
        (when-not grouped?
          [:span {:class "text-xs text-kleene-text-secondary truncate"}
           (:pipeline-group/name group)])]
       (when removed-from-live
         [:div {:class "flex items-center"}
          [alert/alert {:size :xsmall :title "This transform is no longer part of the live pipeline and will be ignored."}]])
       (when actions
         [:div {:class "flex items-center shrink-0"} actions])]]
     [collapsible-content
      [:div {:class "h-96 mt-1"}
       [:f> monaco.diff-editor/diff-editor
        {:original (:live-transform/sql live-transform)
         :modified (:sql transform)
         :language "sql"
         :height   200}]]]]))

(defn- update-group-id [{:keys [group-id transform]}]
  (or group-id (:group-id transform)))

(defn- review-group [{:keys [group-id updates selected-ids set-selected-ids]} & items]
  (let [group          @(re-posh/subscribe [:pipeline-group/pull [:pipeline-group/id group-id]])
        ids            (set (keep :master-id updates))
        selected-count (count (filter #(contains? selected-ids %) ids))
        all-selected?  (= selected-count (count ids))]
    [collapsible-root {:default-open true
                       :class        "mt-2 shrink-0 rounded-lg border border-kleene-input-border overflow-hidden"}
     [collapsible-trigger {:class "gap-2 px-3 py-2 text-sm text-kleene-text bg-kleene-background-secondary radix-state-open:border-b border-kleene-input-border"}
      [:div {:class "flex items-center gap-2 min-w-0"}
       (when set-selected-ids
         [fields/checkbox {:checked   (if (and (pos? selected-count) (not all-selected?))
                                        "indeterminate"
                                        all-selected?)
                           :class     "shrink-0"
                           :on-click  #(.stopPropagation %)
                           :on-change #(set-selected-ids
                                        (fn [s]
                                          (if all-selected?
                                            (apply disj s ids)
                                            (into s ids))))}])
       [:span {:class "truncate font-semibold"} (or (:pipeline-group/name group) "Unknown group")]
       [:span {:class "shrink-0 px-1.5 h-5 flex items-center rounded text-xs bg-kleene-text/10 text-kleene-text-secondary"}
        (count ids)]]]
     (into [collapsible-content {:class "flex flex-col divide-y divide-kleene-input-border"}] items)]))

(defn- grouped-updates
  "Without `set-selected-ids` the groups aren't selectable. `item-props` adds
   per-update props to each row."
  [{:keys [updates item-props selected-ids set-selected-ids]}]
  (let [by-group (group-by update-group-id updates)]
    (into [:<>]
          (for [group-id (distinct (map update-group-id updates))
                :let [group-updates (get by-group group-id)]]
            ^{:key (str group-id)}
            [review-group {:group-id         group-id
                           :updates          group-updates
                           :selected-ids     selected-ids
                           :set-selected-ids set-selected-ids}
             (for [{:keys [master-id] :as update} group-updates]
               ^{:key master-id}
               [update-item (merge update
                                   {:grouped? true}
                                   (when item-props (item-props update)))])]))))

(defn version-preview [{:keys [version]}]
  (let [{:sandbox-version/keys [version-id revisions]} version]
    (react/useEffect
     (fn []
       (dispatch [:sandbox.api/fetch-live-transforms])
       js/undefined)
     #js [])

    [:div {:class "grid grid-rows-[auto_1fr_auto] min-h-full"}
     [:div {:class "sticky top-0 bg-kleene-background px-6 py-4 z-20"}
      [dialog-title "Version preview"]]

     [:div {:class "px-6 py-4 h-[stretch] flex flex-col gap-1"}
      [grouped-updates {:updates revisions}]]

     [dialog-actions {:class "sticky bottom-0 justify-between px-6 py-4 bg-kleene-background items-baseline z-10 mt-0"}
      [typography/p {:class "mt-2 text-kleene-text-secondary"} "Apply all changes above to your running sandbox."]

      [dialog-close
       [:> button/button-fr {:on-click #(dispatch [:sandbox.events/load-version {:version-id version-id}])}
        "Load into sandbox"]]]]))

(defn preview-load-button [{:keys [version]}]
  (let [{:sandbox-version/keys [version-id comment created-at created-by]} version
        query @(re-posh/subscribe [:app-route/prop :app-route/query])
        open (and (= (:popup query) "load-version")
                  (= (:version-id query) version-id))
        set-open #(dispatch [:app.events/navigate-query {:popup (when % "load-version")
                                                         :version-id (when % version-id)}])]
    [dialog-root {:open open
                  :on-open-change set-open}
     [tooltip/tooltip-root
      [tooltip/tooltip-trigger {:as-child true}
       [:div {:role "button"
              :on-click #(set-open true)
              :class "text-left transition-colors p-2 w-full"}
        [typography/small {:class "text-kleene-text-secondary leading-3.5"}
         "Created by " [:strong (:searchable-user/username created-by)] " at " [:strong (format-date created-at)]]
        [typography/h4 {:class "my-auto"} comment]]]
      [tooltip/tooltip-content "Preview version"]]
     [dialog-content {:class "max-w-7xl w-full h-[80vh] overflow-y-auto p-0"}
      [:f> version-preview {:version version}]]]))

(defn remove-version-button [{:keys [version]}]
  (let [{:sandbox-version/keys [version-id]} version
        [open set-open] (react/useState false)]
    [dialog-root {:open open
                  :on-open-change set-open}
     [tooltip/tooltip-root
      [tooltip/tooltip-trigger {:as-child true}
       [:button {:on-click #(set-open true)
                 :class "delete p-2 rounded-r-md text-kleene-text-error hover:bg-kleene-text-error/20 transition-colors"}
        [:> Trash2Icon {:size 20}]]]
      [tooltip/tooltip-content "Delete version"]]

     [dialog-content
      [dialog-title "Are you sure?"]
      [typography/p {:class "mt-2"} "This will permanently delete this version. This action cannot be undone."]
      [dialog-actions
       [button/button {:variant :error
                       :on-click (fn []
                                   (set-open false)
                                   (dispatch [:sandbox.events/delete-version {:version-id version-id}]))}
        "Delete"]]]]))

(defn version-list-item [{:keys [version]}]
  [:li {:class "flex cursor-pointer border border-transparent hover:bg-kleene-hover-background has-[button:hover]:bg-transparent has-[button.preview:hover]:border-kleene-input-border has-[button.delete:hover]:border-kleene-text-error transition-colors rounded-md"}
   [:f> preview-load-button {:version version}]
   [:f> remove-version-button {:version version}]])

(defn sandbox-actions-content [{:keys [versions]}]
  (react/useEffect
   (fn []
     (dispatch [:sandbox.api/fetch-versions])
     js/undefined)
   #js [])

  [:div {:class "overflow-y-auto h-full"}
   [:div {:class "flex flex-col gap-px py-4 px-4 h-full"}
    [typography/h3 {:class "px-2 mb-2"} "Load sandbox version"]
    [:ul {:class "flex flex-col"}
     (for [v versions]
       [version-list-item {:key (:sandbox-version/version-id v)
                           :version v}])]]])

(defn commit-name-form [{:keys [on-submit update-ids]}]
  [form-component/form
   (->form-model
    {:id        :commit-form
     :on-submit (fn [form-data]
                  (let [data (model->fields form-data)]
                    (dispatch [:sandbox.events/commit-sandbox
                               (assoc-some data :update-ids update-ids)])
                    (on-submit)))
     :validate   (fn [fields]
                   (let [comment (get fields :comment)]
                     (not-empty
                      (cond-> {}
                        (str/blank? comment) (assoc :comment ["Comment is required"])))))
     :clean-up  #(dispatch [:form-model/delete [:form-model/id :commit-form]])
     :fields    [{:name          :comment
                  :value         ""}]})
   (fn [fields errors on-submit]
     [:form {:on-submit on-submit
             :class "flex gap-2 items-end"}
      [fields/input {:id :comment
                     :name  "comment"
                     :field (get fields :comment)
                     :errors errors
                     :label "Commit name"
                     :auto-focus true
                     :required true
                     :data-cy "stash-name-input"}]
      [button/button {:type "submit"
                      :data-cy "stash-save-btn"}
       "Save"]])])

(defn stash-name-form [{:keys [on-submit update-ids]}]
  [form-component/form
   (->form-model
    {:id        :stash-version-form
     :on-submit (fn [form-data]
                  (let [data (model->fields form-data)]
                    (dispatch [:sandbox.events/stash-sandbox
                               (assoc-some data :update-ids update-ids)])
                    (on-submit)))
     :validate   (fn [fields]
                   (let [comment (get fields :comment)]
                     (not-empty
                      (cond-> {}
                        (str/blank? comment) (assoc :comment ["Name is required"])))))
     :clean-up  #(dispatch [:form-model/delete [:form-model/id :stash-version-form]])
     :fields    [{:name          :comment
                  :value         ""}]})
   (fn [fields errors on-submit]
     [:form {:on-submit on-submit
             :class "flex gap-2 items-end"}
      [fields/input {:id :comment
                     :name  "name"
                     :field (get fields :comment)
                     :errors errors
                     :label "Version name"
                     :auto-focus true
                     :required true
                     :data-cy "stash-name-input"}]
      [button/button {:type "submit"
                      :data-cy "stash-save-btn"}
       "Save"]])])

(defn stash-button' [{:keys [disabled on-success update-ids]}]
  (let [[open set-open] (react/useState false)]
    [popover-root {:open open
                   :on-open-change set-open}
     [popover-trigger {:as-child true}
      [:> button/button-fr {:variant :empty
                            :disabled disabled
                            :wrapper-class "text-center"}
       [:div {:class "flex items-center justify-center gap-2"}
        [:> Save {:size 16}]
        [:span "Stash"]]]]
     [popover-content {}
      [:div
       [typography/h3 "Stash changes"]
       [typography/p {:class "my-2"} "Save your changes in named version to load it later yourself" [:br] " or by somebody else."]
       [stash-name-form {:update-ids update-ids
                         :on-submit  (fn []
                                       (set-open false)
                                       (when on-success (on-success)))}]]]]))

(defn stash-button [props]
  [:f> stash-button' props])

(defn reset-button' [{:keys [disabled on-success update-ids]}]
  (let [[open set-open] (react/useState false)]
    [popover-root {:open open
                   :on-open-change set-open}
     [popover-trigger {:as-child true}
      [:> button/button-fr {:variant :empty
                            :disabled disabled
                            :class "bg-kleene-text-error/10 text-kleene-text-error text-center hover:bg-kleene-text-error/20"
                            :wrapper-class "text-center"}
       [:div {:class "flex items-center justify-center gap-2"}
        [:> UndoIcon {:size 16}]
        [:span "Reset"]]]]
     [popover-content {:class "max-w-md"}
      [typography/h3 "Are you sure?"]
      [typography/p {:class "mt-2"}
       (if (seq update-ids)
         "Reset will remove the selected changes in the sandbox. "
         "Reset will remove all your unsaved changes in the sandbox. ")
       [:strong "This action cannot be undone."]]
      [popover-actions
       [button/button {:variant :error
                       :on-click (fn []
                                   (dispatch [:sandbox.events/reset-sandbox
                                              (assoc-some {} :update-ids update-ids)])
                                   (set-open false)
                                   (when on-success (on-success)))}
        "Reset"]]]]))

(defn reset-button [props]
  [:f> reset-button' props])


(defn commit-button' [{:keys [disabled on-success can-commit? update-ids]}]
  (let [[open set-open] (react/useState false)]
    [popover-root {:open open
                   :on-open-change set-open}
     [popover-trigger {:as-child true}
      [:> button/button-fr {:disabled (or disabled (not can-commit?))}
       [:div {:class "flex items-center justify-center gap-2"}
        [:> GitCommitHorizontal {:size 16}]
        [:span "Commit"]]]]
     [popover-content {}
      [:div
       [typography/h3 "Commit changes"]
       [typography/p {:class (clsx "my-2" {"text-kleene-text-error" (not can-commit?)})}
        (if can-commit?
          "Committing will apply all changes above to the live pipelines."
          "You don't have permission to commit changes.")]
       (when can-commit?
         [commit-name-form {:update-ids update-ids
                            :on-submit  (fn []
                                          (set-open false)
                                          (when on-success (on-success)))}])]]]))

(defn commit-button [{:keys [disabled on-success update-ids]}]
  (let [can-commit? @(re-posh/subscribe [:sandbox.subs/can-commit?])]
    [:f> commit-button' {:disabled disabled
                         :on-success on-success
                         :can-commit? can-commit?
                         :update-ids update-ids}]))


(defn open-in-new-tab [{:keys [master-id transform]}]
  [tooltip/tooltip-root
   [tooltip/tooltip-trigger
    [button/button {:size      :icon-md
                    :icon      SquareArrowOutUpRightIcon
                    :variant   :empty
                    :component :div
                    :class     "action-button"
                    :on-click (fn [e]
                                (.stopPropagation e)
                                (navigate-transform-tab master-id (:group-id transform) (:name transform)))}]]
   [tooltip/tooltip-content "Open in new tab"]])


(defn review-dialog-content [{:keys [updates loading? on-success]}]
  (let [update-ids                      (vec (keep :master-id updates))
        update-ids-key                  (str (sort update-ids))
        [selected-ids set-selected-ids] (react/useState #{})
        selected-update-ids             (when (seq selected-ids)
                                          (vec selected-ids))
        selected-count                  (count selected-ids)
        no-selection?                   (empty? selected-ids)
        all-selected?                   (and (seq update-ids)
                                             (= selected-count
                                                (count update-ids)))]

    (react/useEffect
     (fn []
       (set-selected-ids
        (fn [ids]
          (set (filter (set update-ids) ids))))
       js/undefined)
     #js [update-ids-key])

    (react/useEffect
     (fn []
       (dispatch [:sandbox.api/fetch-live-transforms])
       js/undefined)
     #js [])

    [:div {:class "grid grid-rows-[auto_1fr_auto] min-h-full"}
     [:div {:class "sticky top-0 bg-kleene-background px-6 py-4 z-20"}
      [dialog-title "Sandbox review"]]
     [:div {:class "px-6 py-4 min-h-0 flex-1 overflow-y-auto overflow-x-hidden flex flex-col gap-1 relative"}
      (cond
        (and loading? (empty? updates))
        [spinner/with-text {:text "Loading sandbox updates..."}]

        (empty? updates)
        [typography/p {:class "text-kleene-text-secondary"} "No changes were made in the sandbox."]

        :else
        [:<>
         [fields/checkbox {:checked (if (and (pos? selected-count)
                                             (not all-selected?))
                                      "indeterminate"
                                      all-selected?)
                           :label     (if all-selected? "Deselect all" "Select all")
                           :class     "px-2 py-2 text-sm text-kleene-text-secondary"
                           :on-change #(set-selected-ids
                                        (if all-selected?
                                          #{}
                                          (set update-ids)))}]

         [grouped-updates
          {:updates          updates
           :selected-ids     selected-ids
           :set-selected-ids set-selected-ids
           :item-props       (fn [{:keys [master-id] :as update}]
                               {:selected? (contains? selected-ids master-id)
                                :on-select #(set-selected-ids
                                             (fn [ids]
                                               (if (contains? ids master-id)
                                                 (disj ids master-id)
                                                 (conj ids master-id))))
                                :actions   [:div {:class "flex gap-2"}
                                            [open-in-new-tab update]]})}]
         (when loading?
           [:div {:class "absolute inset-0 bg-kleene-background/60 flex items-center justify-center z-10"}
            [spinner/with-text {:text "Refreshing..."}]])])]

     [dialog-actions {:class "sticky bottom-0 justify-end px-6 py-4 bg-kleene-background items-baseline z-10 mt-0"}
      [:div {:class "flex gap-1"}
       [reset-button {:disabled no-selection?
                      ;; Selecting everything is a whole-sandbox reset: omit the ids so
                      ;; the backend takes its full-reset path, which also drops the
                      ;; sandbox schema and resyncs from live.
                      :update-ids (when-not all-selected? selected-update-ids)
                      :on-success #(set-selected-ids #{})}]
       [stash-button {:disabled no-selection?
                      :update-ids selected-update-ids
                      :on-success #(set-selected-ids #{})}]
       [commit-button {:disabled no-selection?
                       :update-ids selected-update-ids
                       :on-success on-success}]]]]))

(defn review-dialog' [{{:sandbox-updates/keys [updates]} :updates
                        :keys [loading? query]}]
  (let [open (= (:popup query) "review")
        set-open #(dispatch [:app.events/navigate-query {:popup (when % "review")}])
        close #(set-open false)]
    [dialog-root {:open open
                  :on-open-change set-open
                  :modal false}
     [dialog-content {:modal false
                      :class "max-w-7xl w-[calc(100vw-2rem)] h-[80vh] overflow-y-auto p-0"}
      [:f> review-dialog-content {:updates updates
                                   :loading? loading?
                                   :on-success close}]]]))

(defn review-dialog []
  (let [updates @(re-posh/subscribe [:sandbox-updates/pull])
        loading? @(re-posh/subscribe [:mold-http/loading? :sandbox.api/fetch-sandbox-updates])
        query @(re-posh/subscribe [:app-route/prop :app-route/query])]
    [:f> review-dialog' {:updates updates
                          :loading? loading?
                          :query query}]))

(defn refresh-live-button []
  (let [loading? (or @(re-posh/subscribe [:mold-http/loading? :pipeline-editor.api/fetch-single-kioku-transform])
                     @(re-posh/subscribe [:mold-http/loading? :pipeline-editor.api/fetch-live-table])
                     @(re-posh/subscribe [:mold-http/loading? :pipeline-editor.api/fetch-transform-groups])
                     @(re-posh/subscribe [:mold-http/loading? :dag.api/fetch-graph])
                     @(re-posh/subscribe [:mold-http/loading? :dag.api/load-dag-metadata]))]
    [tooltip/tooltip-root
     [tooltip/tooltip-trigger
      [button/button {:size       :icon
                      :icon       RotateCw
                      :icon-props {:size 24}
                      :variant    :empty-transparent
                      :loading    loading?
                      :component  :div
                      :on-click   #(dispatch [:pipeline-editor.events/refresh-live-data])}]]
     [tooltip/tooltip-content "Refresh live data"]]))


;; Providers must pass a #js object, not a CLJS map: consumers read it via
;; js-interop (jsi/get/jsi/call), which does raw property access and would
;; silently see nil/undefined for every key on a plain CLJS map.
;; Expected shape: #js {:features {...} :enabled (fn [flag] ...) :read-only? bool}
(def features-context (react/createContext nil))
